"use client";

import React, { useCallback } from "react";
import { useRouter } from "next/navigation";
import { DetailContextProvider } from "@/contexts/DetailContextProvider";
import { ContactDetailsTab } from "@/components/shared/detail-tabs/ContactDetailsTab";
import { useContact } from "@/lib/hooks/use-api";
import type { TabContentProps } from "@/components/shared/UniversalDetailPage/types/detailPage";
import type { ContactTaskRow } from "@/lib/types/entity-rows";

/**
 * Task Contact Display Tab Component
 *
 * Wraps the reusable ContactDetailsTab with task-specific context resolution.
 */
export function TaskContactDisplay({ item }: TabContentProps<ContactTaskRow>) {
  const router = useRouter();
  const resolvedFromCommunication = (
    item as ContactTaskRow & { effectiveLogActivityContactId?: string }
  ).effectiveLogActivityContactId;
  // Use entity_id when entity_type is 'contacts', otherwise contact_id or resolved communication contact
  const contactId =
    item.entity_type === "contacts"
      ? item.entity_id
      : resolvedFromCommunication ?? item.contact_id;

  const { data: contact } = useContact(contactId || "");

  const handleViewFullContact = useCallback(
    (id: string) => {
      router.push(`/contacts/${id}`);
    },
    [router]
  );

  return (
    <DetailContextProvider
      parentEntity={{
        type: "contact",
        id: contact?.id || contactId || "",
        name: typeof contact?.name === "string" ? contact.name : undefined,
      }}
    >
      <ContactDetailsTab
        contactId={contactId}
        contactData={contact}
        onViewFullContact={handleViewFullContact}
        showDescription="Contact information for this task"
      />
    </DetailContextProvider>
  );
}
